import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import eventHeroDesktop from "../assets/Eventos/events-hero-loop-desktop.webp";
import eventHeroMobile from "../assets/Eventos/events-hero-loop-mobile.webp";
import eventKidsImage from "../assets/Eventos/Animacion.png";
import eventPhotoImage from "../assets/Eventos/fotografia.jpg";

const EVENTS_CACHE_KEY = "loop_events_businesses_cache_v2";

export const EVENT_CATEGORIES = [
  {
    id: "infantiles",
    title: "Infantiles",
    subtitle: "Personajes, shows, animación y magia para cumpleaños.",
    emoji: "🎈",
    imageUrl: eventKidsImage,
    image: "linear-gradient(135deg, rgba(255,247,237,.78) 0%, rgba(254,243,199,.72) 35%, rgba(236,253,245,.82) 100%)",
    tags: ["princesas", "superheroes", "frozen", "personajes", "magia", "pintacaritas", "cumpleanos", "cumpleaños"],
  },
  {
    id: "fotografia",
    title: "Fotografía & eventos",
    subtitle: "Fotógrafos, video, contenido, cobertura y producción.",
    emoji: "📸",
    imageUrl: eventPhotoImage,
    image: "linear-gradient(135deg, rgba(239,246,255,.82) 0%, rgba(248,250,252,.72) 42%, rgba(236,253,245,.82) 100%)",
    tags: ["fotografia", "fotografía", "video", "reels", "eventos", "cobertura", "produccion", "producción"],
  },
];

export const TRENDING_EXPERIENCES = [
  { id: "frozen", title: "Frozen", subtitle: "Princesas y show musical", emoji: "❄️", category: "infantiles" },
  { id: "superheroes", title: "Superhéroes", subtitle: "Shows de acción para niños", emoji: "🦸", category: "infantiles" },
  { id: "pintacaritas", title: "Pintacaritas", subtitle: "Extras para fiestas", emoji: "🎨", category: "infantiles" },
  { id: "fotografia", title: "Fotografía", subtitle: "Cobertura profesional", emoji: "📸", category: "fotografia" },
  { id: "video", title: "Video & reels", subtitle: "Contenido para redes", emoji: "🎥", category: "fotografia" },
];


function isPublicBusiness(biz) {
  if (!biz || typeof biz !== "object") return false;
  return (
    biz.visible !== false &&
    biz.hidden !== true &&
    biz.isActive !== false &&
    biz.published !== false
  );
}

function safeText(...values) {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v != null && typeof v !== "string") return v;
  }
  return "";
}

function readJsonCache(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch (_) {
    return fallback;
  }
}

function writeJsonCache(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
}

function normalizeText(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

export function normalizeEventBusiness(raw) {
  const b = raw || {};
  const rawCategoryText = [
    b.eventCategory,
    b.eventSubtype,
    b.eventType,
    b.subtype,
    b.eventTags,
    b.tags,
    b.category,
    b.businessType,
    b.description,
    b.about,
    b.name,
  ].flat().filter(Boolean).join(" ");
  const normalizedRawCategoryText = normalizeText(rawCategoryText);

  const name = safeText(b.name, b.businessName, b.title, "Evento");
  let eventCategory = normalizeText(
    b.eventCategory ||
    b.eventSubtype ||
    b.eventType ||
    b.subtype ||
    ""
  );

  if (!eventCategory || eventCategory === "eventos" || eventCategory === "events") {
    if (
      normalizedRawCategoryText.includes("infantil") ||
      normalizedRawCategoryText.includes("animacion") ||
      normalizedRawCategoryText.includes("animación") ||
      normalizedRawCategoryText.includes("personaje") ||
      normalizedRawCategoryText.includes("cumpleano") ||
      normalizedRawCategoryText.includes("cumpleaño") ||
      normalizedRawCategoryText.includes("princesa") ||
      normalizedRawCategoryText.includes("frozen") ||
      normalizedRawCategoryText.includes("superheroe") ||
      normalizedRawCategoryText.includes("superhéroe")
    ) {
      eventCategory = "infantiles";
    } else if (
      normalizedRawCategoryText.includes("fotografia") ||
      normalizedRawCategoryText.includes("fotografía") ||
      normalizedRawCategoryText.includes("foto") ||
      normalizedRawCategoryText.includes("video") ||
      normalizedRawCategoryText.includes("reels") ||
      normalizedRawCategoryText.includes("cobertura")
    ) {
      eventCategory = "fotografia";
    }
  }
  const tags = [
    eventCategory,
    b.eventCategory,
    b.eventSubtype,
    b.eventType,
    b.subtype,
    ...(Array.isArray(b.eventTags) ? b.eventTags : []),
    ...(Array.isArray(b.characters) ? b.characters : []),
    ...(Array.isArray(b.tags) ? b.tags : []),
    ...(Array.isArray(b.services) ? b.services.map((s) => (typeof s === "string" ? s : s?.name)) : []),
  ].filter(Boolean).map((x) => normalizeText(x));

  const location = safeText(b.coverageArea, b.city, b.address, b.location, b.serviceArea, b.area, "Panamá");
  const modeBadge = eventCategory === "infantiles" ? "Infantiles" : eventCategory === "fotografia" ? "Fotografía" : "Eventos";
  const servicePreview = Array.isArray(b.servicePreview) ? b.servicePreview : [];
  const services = Array.isArray(b.services) ? b.services : [];
  const packages = servicePreview.length
    ? servicePreview.map((s, idx) => ({ id: s.id || `service-${idx}`, title: s.name || s.title || "Paquete", duration: s.duration ? `${s.duration} min` : "A consultar", price: s.price ? `$${s.price}` : "Cotizar" }))
    : services.slice(0, 2).map((s, idx) => ({ id: s?.id || `service-${idx}`, title: typeof s === "string" ? s : s?.name || "Paquete", duration: s?.duration ? `${s.duration} min` : "A consultar", price: s?.price ? `$${s.price}` : "Cotizar" }));

  return {
    id: b.id || b.businessId,
    businessId: b.id || b.businessId,
    name,
    tagline: safeText(b.tagline, ""),
    rating: Number(b.rating || b.avgRating || 0) || null,
    reviews: Number(b.reviews || b.reviewCount || 0) || 0,
    fromPrice: b.fromPrice || b.startingPrice || b.priceFrom || null,
    location,
    category: eventCategory || "eventos",
    eventCategory,
    tags,
    badges: [modeBadge],
    characters: Array.isArray(b.characters) && b.characters.length ? b.characters : ["Eventos", "Experiencias"],
    packages: packages.length ? packages : [{ id: "quote", title: "Solicitar cotización", duration: "A consultar", price: "Cotizar" }],
    imageUrl: b.logoUrl || b.logo || b.logoImage || b.menuImage || null,
    imageDisplayMode: (b.logoUrl || b.logo || b.logoImage) ? "logo" : "photo",
    raw: b,
  };
}

async function fetchEventBusinesses() {
  const queries = [
    query(collection(db, "businesses"), where("businessType", "in", ["events", "eventos", "Eventos"]), limit(80)),
    query(collection(db, "businesses"), where("type", "in", ["events", "eventos", "Eventos"]), limit(80)),
  ];

  const byId = new Map();

  for (const qBuilt of queries) {
    try {
      const snap = await getDocs(qBuilt);
      snap.docs.forEach((d) => {
        const raw = { id: d.id, ...(d.data() || {}) };
        if (raw.visible === false || raw.hidden === true || raw.isActive === false) return;
        byId.set(d.id, raw);
      });
    } catch (e) {
      console.warn("[EventsHomePage] query failed", e);
    }
  }

  return Array.from(byId.values())
    .map((raw) => normalizeEventBusiness(raw))
    .filter((b) => b.id);
}

export function categoryMatchesBusiness(category, business) {
  const c = normalizeText(category.id);
  const raw = business?.raw || {};
  const haystack = [
    business.eventCategory,
    business.category,
    business.name,
    business.tagline,
    raw.eventCategory,
    raw.eventSubtype,
    raw.eventType,
    raw.subtype,
    raw.category,
    raw.businessType,
    raw.description,
    raw.about,
    ...(Array.isArray(business.tags) ? business.tags : []),
    ...(Array.isArray(raw.eventTags) ? raw.eventTags : []),
    ...(Array.isArray(raw.tags) ? raw.tags : []),
  ].filter(Boolean).join(" ");
  const text = normalizeText(haystack);

  if (normalizeText(business.eventCategory) === c) return true;
  if (normalizeText(raw.eventCategory) === c) return true;
  if (normalizeText(raw.eventSubtype) === c) return true;
  if (normalizeText(raw.eventType) === c) return true;
  if (normalizeText(raw.subtype) === c) return true;

  if (c === "infantiles") {
    return [
      "infantil",
      "infantiles",
      "animacion",
      "animación",
      "personaje",
      "personajes",
      "cumpleano",
      "cumpleaño",
      "princesa",
      "princesas",
      "frozen",
      "superheroe",
      "superhéroe",
      "pintacaritas",
      "magia",
      "kids",
    ].some((word) => text.includes(normalizeText(word)));
  }

  if (c === "fotografia") {
    return [
      "fotografia",
      "fotografía",
      "foto",
      "fotos",
      "photo",
      "video",
      "reels",
      "contenido",
      "cobertura",
      "produccion",
      "producción",
    ].some((word) => text.includes(normalizeText(word)));
  }

  const categoryTags = (category.tags || []).map(normalizeText);
  return business.tags?.some((t) => categoryTags.includes(normalizeText(t))) || false;
}

const eventStyles = `
  @import url("https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600&family=Inter:wght@400;500;600;700;800&display=swap");
  .events-page{min-height:100vh;background:radial-gradient(circle at top left,rgba(0,103,95,.10),transparent 32%),radial-gradient(circle at top right,rgba(236,72,153,.08),transparent 28%),linear-gradient(180deg,#f8fafc 0%,#fff 48%,#f8fafc 100%);color:#0f172a;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;padding-bottom:calc(88px + env(safe-area-inset-bottom,0px))}.events-shell{width:min(100%,1460px);margin:0 auto;padding:34px clamp(18px,5vw,70px);box-sizing:border-box}.events-search-card{min-height:74px;border:1px solid rgba(226,232,240,.95);border-radius:999px;background:rgba(255,255,255,.92);box-shadow:0 18px 48px rgba(15,23,42,.08);display:grid;grid-template-columns:repeat(3,minmax(0,1fr)) 58px;align-items:center;overflow:hidden;margin-bottom:28px}.events-search-field{padding:0 24px;border-right:1px solid #e2e8f0}.events-search-field strong{display:block;font-size:12px;font-weight:950;color:#0f172a}.events-search-field span{display:block;margin-top:3px;color:#64748b;font-size:13px;font-weight:700}.events-search-button{width:48px;height:48px;border-radius:999px;border:0;background:linear-gradient(135deg,#00675f,#00897f);color:#fff;font-size:20px;font-weight:950;box-shadow:0 12px 28px rgba(0,103,95,.22);cursor:pointer}.events-hero{display:grid;grid-template-columns:minmax(0,1.05fr) minmax(320px,.72fr);gap:clamp(28px,5vw,72px);align-items:center;padding:6px 0 38px}.events-eyebrow,.events-section-kicker{color:#008878;font-size:12px;font-weight:950;letter-spacing:.16em;text-transform:uppercase;margin-bottom:12px}.events-hero h1{max-width:760px;margin:0;font-size:clamp(44px,5.7vw,78px);line-height:.94;letter-spacing:-.075em;font-weight:950}.events-hero p{max-width:690px;margin:20px 0 0;color:#475569;font-size:17px;line-height:1.55;font-weight:750}.events-hero-actions{display:flex;align-items:center;flex-wrap:wrap;gap:14px;margin-top:28px}.events-primary{border:0;border-radius:999px;min-height:56px;padding:0 24px;background:linear-gradient(135deg,#00675f,#00897f);color:#fff;font-weight:950;box-shadow:0 16px 34px rgba(0,103,95,.24);cursor:pointer}.events-hero-actions span{color:#64748b;font-size:13px;font-weight:850}.events-hero-visual{min-height:390px;border-radius:42px;border:1px solid rgba(226,232,240,.95);background:radial-gradient(circle at 20% 20%,rgba(255,255,255,.9),transparent 24%),linear-gradient(135deg,#fff 0%,#ecfdf5 46%,#fef3c7 100%);box-shadow:0 30px 80px rgba(15,23,42,.12);position:relative;overflow:hidden;display:grid;place-items:center}.events-hero-img{width:100%;height:100%;min-height:390px;object-fit:cover;object-position:center;display:block}.events-big-emoji{font-size:120px;filter:drop-shadow(0 20px 30px rgba(15,23,42,.12))}.events-floating-chip{position:absolute;border-radius:28px;background:rgba(255,255,255,.86);border:1px solid rgba(226,232,240,.9);box-shadow:0 18px 44px rgba(15,23,42,.12);padding:14px 16px;display:grid;gap:4px}.events-floating-chip strong{font-size:16px;font-weight:950}.events-floating-chip span{color:#64748b;font-size:12px;font-weight:800}.chip-one{left:24px;bottom:30px}.chip-two{right:22px;top:28px}.events-section{padding:34px 0;border-top:1px solid rgba(203,213,225,.85)}.events-section-head{display:flex;align-items:flex-end;justify-content:space-between;gap:18px;margin-bottom:22px}.events-section-title{margin:0;color:#0f172a;font-size:clamp(30px,3.2vw,46px);line-height:1.02;font-weight:950;letter-spacing:-.058em}.events-section-copy{margin:8px 0 0;color:#64748b;font-size:15px;line-height:1.5;font-weight:750}.events-category-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px}.events-category-card{min-height:250px;border:1px solid rgba(226,232,240,.96);border-radius:36px;background:#fff;box-shadow:0 20px 54px rgba(15,23,42,.08);padding:24px;text-align:left;cursor:pointer;display:grid;align-content:space-between;overflow:hidden;position:relative}.events-category-card::before{content:"";position:absolute;inset:0;background:var(--event-bg);opacity:.95;z-index:0}.events-category-card>*{position:relative;z-index:1}.events-category-emoji{font-size:56px}.events-category-media{width:100%;height:118px;border-radius:26px;overflow:hidden;background:rgba(255,255,255,.54);border:1px solid rgba(255,255,255,.62);box-shadow:inset 0 1px 0 rgba(255,255,255,.65);display:grid;place-items:center}.events-category-media img{width:100%;height:100%;object-fit:cover;object-position:center;display:block}.events-category-media span{font-size:54px}.events-category-card h3{margin:20px 0 8px;font-size:34px;line-height:1.02;font-weight:950;letter-spacing:-.06em}.events-category-card p{max-width:560px;margin:0;color:#475569;font-size:15px;line-height:1.5;font-weight:750}.events-category-card footer{margin-top:20px;display:flex;align-items:center;justify-content:space-between;gap:12px;color:#00675f;font-size:13px;font-weight:950}.events-horizontal-row{display:grid;grid-auto-flow:column;grid-auto-columns:minmax(230px,1fr);gap:14px;overflow-x:auto;padding-bottom:10px;scroll-snap-type:x mandatory}.events-trend-card{scroll-snap-align:start;min-height:178px;border-radius:32px;border:1px solid rgba(226,232,240,.95);background:#fff;box-shadow:0 16px 42px rgba(15,23,42,.065);padding:18px;display:grid;align-content:space-between;text-align:left;cursor:pointer}.events-trend-art{height:86px;border-radius:24px;background:linear-gradient(135deg,#ecfdf5,#f8fafc);display:grid;place-items:center;font-size:42px;margin-bottom:12px}.events-trend-card strong{font-size:18px;font-weight:950;letter-spacing:-.04em}.events-trend-card span{display:block;margin-top:5px;color:#64748b;font-size:13px;font-weight:750}.events-business-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}.events-business-card{overflow:hidden;border-radius:32px;border:1px solid rgba(226,232,240,.95);background:#fff;box-shadow:0 18px 46px rgba(15,23,42,.08);cursor:pointer;transition:transform 160ms ease,box-shadow 160ms ease}.events-business-card:hover{transform:translateY(-2px);box-shadow:0 24px 60px rgba(15,23,42,.12)}.events-business-cover{min-height:225px;display:flex;align-items:flex-end;justify-content:space-between;padding:16px;box-sizing:border-box;background-size:cover!important;background-position:center!important}.events-cover-badge{border-radius:999px;background:rgba(255,255,255,.92);color:#0f172a;padding:9px 13px;font-size:13px;font-weight:950;box-shadow:0 12px 28px rgba(15,23,42,.10)}.events-business-body{padding:18px}.events-business-meta{display:flex;justify-content:space-between;gap:12px;color:#64748b;font-size:12px;font-weight:850}.events-business-body h3{margin:12px 0 7px;font-size:24px;line-height:1.03;font-weight:950;letter-spacing:-.052em}.events-business-body p{margin:0;color:#64748b;font-size:13.5px;line-height:1.45;font-weight:700}.events-badges{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px}.events-badges span{border-radius:999px;background:#ecfdf5;color:#00675f;padding:7px 10px;font-size:12px;font-weight:900}.events-packages-mini{display:grid;gap:8px;margin-top:14px}.events-packages-mini div{border:1px solid #e2e8f0;border-radius:16px;padding:10px;background:#f8fafc}.events-packages-mini strong{display:block;color:#0f172a;font-size:13px;font-weight:950}.events-packages-mini span{display:block;color:#64748b;font-size:12px;font-weight:800;margin-top:3px}.events-loading-card,.events-empty-card{border:1px solid rgba(226,232,240,.95);border-radius:28px;padding:22px;background:#fff;color:#64748b;font-weight:800;box-shadow:0 16px 42px rgba(15,23,42,.055)}@media(max-width:980px){.events-shell{padding:18px 16px 108px}.events-search-card{border-radius:28px;grid-template-columns:1fr;padding:10px;gap:8px}.events-search-field{border-right:0;border-bottom:1px solid #e2e8f0;padding:10px 14px}.events-search-button{width:100%;border-radius:20px}.events-hero{display:block;padding-top:8px}.events-hero h1{font-size:42px}.events-hero p{font-size:15px;margin-top:15px}.events-hero-visual{min-height:250px;margin-top:26px;border-radius:32px}.events-hero-img{min-height:250px}.events-big-emoji{font-size:86px}.events-floating-chip{display:none}.events-section{padding:24px 0}.events-section-head{display:block}.events-category-grid{grid-template-columns:1fr}.events-category-card{min-height:260px;border-radius:30px}.events-category-media{height:130px;border-radius:24px}.events-category-card h3{font-size:30px}.events-business-grid{grid-template-columns:1fr}.events-horizontal-row{grid-auto-columns:minmax(210px,74vw)}}

  /* FINAL FIX: full-image category cards with text overlay */
  .events-category-card{
    min-height:320px !important;
    padding:28px !important;
    display:flex !important;
    flex-direction:column !important;
    justify-content:flex-end !important;
    align-content:unset !important;
    background:#000 !important;
    overflow:hidden !important;
  }
  .events-category-card::before{
    content:"" !important;
    position:absolute !important;
    inset:0 !important;
    background:linear-gradient(180deg,rgba(15,23,42,.03) 0%,rgba(15,23,42,.16) 38%,rgba(15,23,42,.78) 100%) !important;
    opacity:1 !important;
    z-index:1 !important;
    pointer-events:none !important;
  }
  .events-category-card > div{
    position:static !important;
    z-index:auto !important;
  }
  .events-category-media{
    position:absolute !important;
    inset:0 !important;
    width:100% !important;
    height:100% !important;
    border-radius:0 !important;
    border:0 !important;
    background:#000 !important;
    box-shadow:none !important;
    display:block !important;
    z-index:0 !important;
  }
  .events-category-media img{
    width:100% !important;
    height:100% !important;
    object-fit:cover !important;
    object-position:center center !important;
    display:block !important;
    padding:0 !important;
  }
  .events-category-card h3,
  .events-category-card p,
  .events-category-card footer{
    position:relative !important;
    z-index:2 !important;
  }
  .events-category-card h3{
    margin:0 0 10px !important;
    color:#fff !important;
    font-size:40px !important;
    line-height:.96 !important;
    letter-spacing:-.07em !important;
    text-shadow:0 10px 30px rgba(0,0,0,.35) !important;
  }
  .events-category-card p{
    color:rgba(255,255,255,.92) !important;
    font-weight:850 !important;
    text-shadow:0 8px 24px rgba(0,0,0,.24) !important;
  }
  .events-category-card footer{
    color:#fff !important;
    margin-top:22px !important;
  }
  .events-category-card footer span:last-child{
    border:1px solid rgba(255,255,255,.68) !important;
    border-radius:999px !important;
    padding:10px 16px !important;
    background:rgba(255,255,255,.08) !important;
    backdrop-filter:blur(10px) !important;
    -webkit-backdrop-filter:blur(10px) !important;
  }
  @media(max-width:980px){
    .events-category-card{min-height:300px !important;border-radius:30px !important;}
    .events-category-card h3{font-size:34px !important;}
  }


  /* =========================================================
     LOOP EVENTS HERO PARITY V1
     Same visual family as MainMenu Discovery Hero V76:
     - full-bleed image hero
     - deep Loop green overlay
     - Cormorant Garamond editorial headline
     - Inter supporting copy
     - cream pill CTA
     - one responsive contract across web/native
     Scope: Events hero only.
     ========================================================= */
  .events-discovery-hero-v1{
    width:100%!important;
    max-width:none!important;
    height:clamp(500px,36.8vw,630px)!important;
    min-height:clamp(500px,36.8vw,630px)!important;
    max-height:630px!important;
    margin:-2px 0 0!important;
    border:0!important;
    border-radius:0!important;
    overflow:hidden!important;
    position:relative!important;
    background-image:
      linear-gradient(
        90deg,
        rgba(0,48,42,.98) 0%,
        rgba(0,48,42,.95) 22%,
        rgba(0,48,42,.88) 34%,
        rgba(0,48,42,.68) 46%,
        rgba(0,48,42,.36) 58%,
        rgba(0,48,42,.12) 72%,
        rgba(0,48,42,.03) 100%
      ),
      var(--events-hero-image)!important;
    background-size:cover,cover!important;
    background-position:center center,center 47%!important;
    background-repeat:no-repeat,no-repeat!important;
    background-color:#003b35!important;
    box-shadow:none!important;
  }

  .events-discovery-hero-v1::before,
  .events-discovery-hero-v1::after{
    display:none!important;
    content:none!important;
  }

  .events-discovery-hero-overlay-v1{
    width:100%!important;
    max-width:1700px!important;
    height:100%!important;
    min-height:100%!important;
    margin:0 auto!important;
    padding:
      clamp(54px,5.2vw,88px)
      clamp(28px,8.9vw,154px)
      clamp(46px,4.8vw,78px)!important;
    box-sizing:border-box!important;
    display:flex!important;
    align-items:center!important;
    justify-content:flex-start!important;
    overflow:visible!important;
  }

  .events-discovery-copy-v1{
    width:min(700px,50vw)!important;
    max-width:700px!important;
    min-width:0!important;
    margin:0!important;
    padding:0!important;
    display:block!important;
    color:#fff8ee!important;
    position:relative!important;
    z-index:2!important;
  }

  .events-discovery-eyebrow-v1{
    margin:0 0 22px!important;
    color:rgba(255,248,238,.90)!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:13px!important;
    line-height:1!important;
    font-weight:800!important;
    letter-spacing:.16em!important;
    text-transform:uppercase!important;
  }

  .events-discovery-title-v1{
    width:100%!important;
    max-width:700px!important;
    margin:0!important;
    color:#fff8ee!important;
    font-family:"Cormorant Garamond","Playfair Display",Georgia,"Times New Roman",serif!important;
    font-size:clamp(66px,4.9vw,86px)!important;
    line-height:.91!important;
    letter-spacing:-.038em!important;
    font-weight:500!important;
    text-wrap:balance!important;
    text-shadow:0 3px 20px rgba(0,0,0,.18)!important;
  }

  .events-discovery-title-v1 br{
    display:block!important;
  }

  .events-discovery-subtitle-v1{
    width:min(100%,590px)!important;
    max-width:590px!important;
    margin:24px 0 0!important;
    color:rgba(255,248,238,.94)!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:clamp(16px,1.12vw,19px)!important;
    line-height:1.48!important;
    letter-spacing:-.018em!important;
    font-weight:500!important;
    text-shadow:0 2px 12px rgba(0,0,0,.15)!important;
  }

  .events-discovery-actions-v1{
    width:100%!important;
    margin-top:30px!important;
    display:grid!important;
    justify-items:start!important;
    gap:15px!important;
  }

  .events-discovery-primary-v1{
    min-width:300px!important;
    min-height:60px!important;
    padding:0 24px 0 28px!important;
    border:1px solid rgba(255,255,255,.34)!important;
    border-radius:999px!important;
    background:#fff8ee!important;
    color:#004c44!important;
    box-shadow:0 18px 42px rgba(0,0,0,.16)!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:28px!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:16px!important;
    line-height:1!important;
    font-weight:800!important;
    letter-spacing:-.02em!important;
    cursor:pointer!important;
    transition:transform 160ms ease,box-shadow 160ms ease,background 160ms ease!important;
  }

  .events-discovery-primary-v1:hover{
    transform:translateY(-1px)!important;
    background:#fff!important;
    box-shadow:0 22px 48px rgba(0,0,0,.20)!important;
  }

  .events-discovery-primary-v1:active{
    transform:scale(.987)!important;
  }

  .events-discovery-primary-arrow-v1{
    font-size:25px!important;
    line-height:1!important;
    font-weight:500!important;
    transform:translateY(-1px)!important;
  }

  .events-discovery-support-v1{
    max-width:520px!important;
    color:rgba(255,248,238,.86)!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:13.5px!important;
    line-height:1.4!important;
    font-weight:600!important;
    letter-spacing:-.012em!important;
    text-shadow:0 2px 10px rgba(0,0,0,.14)!important;
  }

  .events-shell-after-hero-v1{
    padding-top:18px!important;
  }

  .events-shell-after-hero-v1 > .events-section:first-child{
    border-top:0!important;
    padding-top:28px!important;
  }

  /* Mid desktop / web tablet */
  @media(min-width:900px) and (max-width:1180px){
    .events-discovery-hero-v1{
      height:clamp(500px,52vw,580px)!important;
      min-height:clamp(500px,52vw,580px)!important;
      max-height:580px!important;
      background-position:center center,58% 48%!important;
    }

    .events-discovery-hero-overlay-v1{
      padding:52px clamp(30px,6vw,66px) 48px!important;
    }

    .events-discovery-copy-v1{
      width:min(650px,65vw)!important;
      max-width:650px!important;
    }

    .events-discovery-title-v1{
      max-width:630px!important;
      font-size:clamp(61px,7vw,76px)!important;
    }

    .events-discovery-subtitle-v1{
      max-width:520px!important;
      font-size:16px!important;
    }
  }

  /* Browser mobile + native compact layout */
  @media(max-width:899px){
    .events-discovery-hero-v1,
    body.is-mobile-app .events-discovery-hero-v1,
    html.is-mobile-app .events-discovery-hero-v1{
      height:auto!important;
      min-height:500px!important;
      max-height:none!important;
      margin-top:-2px!important;
      overflow:hidden!important;
      background-image:
        linear-gradient(
          180deg,
          rgba(0,48,42,.93) 0%,
          rgba(0,48,42,.88) 43%,
          rgba(0,48,42,.72) 70%,
          rgba(0,48,42,.55) 100%
        ),
        var(--events-hero-image)!important;
      background-size:cover,cover!important;
      background-position:center center,62% 50%!important;
    }

    .events-discovery-hero-overlay-v1,
    body.is-mobile-app .events-discovery-hero-overlay-v1,
    html.is-mobile-app .events-discovery-hero-overlay-v1{
      width:100%!important;
      height:auto!important;
      min-height:500px!important;
      display:flex!important;
      align-items:center!important;
      padding:42px 18px 34px!important;
    }

    .events-discovery-copy-v1{
      width:100%!important;
      max-width:620px!important;
    }

    .events-discovery-eyebrow-v1{
      margin-bottom:18px!important;
      font-size:11px!important;
      letter-spacing:.15em!important;
    }

    .events-discovery-title-v1,
    body.is-mobile-app .events-discovery-title-v1,
    html.is-mobile-app .events-discovery-title-v1{
      width:100%!important;
      max-width:560px!important;
      font-size:clamp(48px,13vw,64px)!important;
      line-height:.92!important;
      letter-spacing:-.036em!important;
      font-weight:500!important;
    }

    .events-discovery-subtitle-v1{
      width:100%!important;
      max-width:520px!important;
      margin-top:19px!important;
      font-size:14.5px!important;
      line-height:1.45!important;
    }

    .events-discovery-actions-v1{
      width:100%!important;
      margin-top:25px!important;
      gap:14px!important;
    }

    .events-discovery-primary-v1{
      width:min(100%,360px)!important;
      min-width:0!important;
      min-height:54px!important;
      padding:0 20px 0 23px!important;
      font-size:14.5px!important;
    }

    .events-discovery-support-v1{
      max-width:360px!important;
      font-size:12.5px!important;
    }

    .events-shell-after-hero-v1{
      padding-top:8px!important;
    }

    .events-shell-after-hero-v1 > .events-section:first-child{
      padding-top:22px!important;
    }
  }

  @media(max-width:430px){
    .events-discovery-hero-v1{
      min-height:520px!important;
      background-position:center center,64% 50%!important;
    }

    .events-discovery-hero-overlay-v1{
      min-height:520px!important;
      padding-top:36px!important;
      padding-bottom:30px!important;
    }

    .events-discovery-title-v1{
      font-size:clamp(45px,12.8vw,56px)!important;
    }
  }



  /* =========================================================
     LOOP EVENTS HERO IMAGE FIX V1.1
     Real image layer: robust for asset filenames with spaces and
     easier responsive focal-point control.
     ========================================================= */
  .events-discovery-hero-v11{
    background:#003b35!important;
    background-image:none!important;
    isolation:isolate!important;
  }

  .events-discovery-bg-v11{
    position:absolute!important;
    inset:0!important;
    width:100%!important;
    height:100%!important;
    display:block!important;
    object-fit:cover!important;
    object-position:center 47%!important;
    z-index:0!important;
    pointer-events:none!important;
    user-select:none!important;
    transform:translateZ(0)!important;
  }

  .events-discovery-hero-v11 .events-discovery-hero-overlay-v1{
    position:relative!important;
    z-index:1!important;
    background:
      linear-gradient(
        90deg,
        rgba(0,48,42,.98) 0%,
        rgba(0,48,42,.95) 22%,
        rgba(0,48,42,.88) 34%,
        rgba(0,48,42,.68) 46%,
        rgba(0,48,42,.36) 58%,
        rgba(0,48,42,.12) 72%,
        rgba(0,48,42,.03) 100%
      )!important;
  }

  @media(min-width:900px) and (max-width:1180px){
    .events-discovery-bg-v11{
      object-position:58% 48%!important;
    }
  }

  @media(max-width:899px){
    .events-discovery-bg-v11{
      object-position:62% 50%!important;
    }

    .events-discovery-hero-v11 .events-discovery-hero-overlay-v1{
      background:
        linear-gradient(
          180deg,
          rgba(0,48,42,.94) 0%,
          rgba(0,48,42,.88) 43%,
          rgba(0,48,42,.72) 70%,
          rgba(0,48,42,.55) 100%
        )!important;
    }
  }

  @media(max-width:430px){
    .events-discovery-bg-v11{
      object-position:64% 50%!important;
    }
  }

  /* =========================================================
     LOOP EVENTS CATEGORIES POLISH V2
     Editorial section + premium full-image cards.
     Desktop/tablet/mobile share the same card system.
     ========================================================= */
  .events-categories-v2{
    width:min(100%,1240px)!important;
    margin:0 auto!important;
    padding:42px 0 46px!important;
    border-top:0!important;
  }

  .events-categories-head-v2{
    margin-bottom:24px!important;
  }

  .events-categories-title-v2{
    max-width:780px!important;
    margin:0!important;
    color:#0f172a!important;
    font-family:"Cormorant Garamond","Playfair Display",Georgia,"Times New Roman",serif!important;
    font-size:clamp(48px,4.25vw,64px)!important;
    line-height:.94!important;
    letter-spacing:-.035em!important;
    font-weight:500!important;
    text-wrap:balance!important;
  }

  .events-categories-copy-v2{
    max-width:650px!important;
    margin-top:11px!important;
    color:#64748b!important;
    font-size:14px!important;
    line-height:1.48!important;
    font-weight:650!important;
    letter-spacing:-.012em!important;
  }

  .events-category-grid-v2{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:22px!important;
  }

  .events-category-card-v2{
    min-height:360px!important;
    height:auto!important;
    padding:26px!important;
    border:1px solid rgba(226,232,240,.88)!important;
    border-radius:30px!important;
    overflow:hidden!important;
    position:relative!important;
    display:flex!important;
    flex-direction:column!important;
    justify-content:flex-end!important;
    align-items:stretch!important;
    text-align:left!important;
    background:#0f172a!important;
    box-shadow:0 18px 44px rgba(15,23,42,.10)!important;
    transition:transform 180ms ease,box-shadow 180ms ease,border-color 180ms ease!important;
  }

  .events-category-card-v2::before{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    z-index:1!important;
    background:
      linear-gradient(
        180deg,
        rgba(10,20,27,.04) 0%,
        rgba(10,20,27,.12) 35%,
        rgba(10,20,27,.70) 76%,
        rgba(8,18,24,.93) 100%
      )!important;
    opacity:1!important;
    pointer-events:none!important;
  }

  .events-category-card-v2::after{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    z-index:1!important;
    border-radius:inherit!important;
    box-shadow:inset 0 0 0 1px rgba(255,255,255,.07)!important;
    pointer-events:none!important;
  }

  .events-category-card-v2 .events-category-media{
    position:absolute!important;
    inset:0!important;
    z-index:0!important;
    width:100%!important;
    height:100%!important;
    border:0!important;
    border-radius:0!important;
    overflow:hidden!important;
    background:#0f172a!important;
    box-shadow:none!important;
  }

  .events-category-card-v2 .events-category-media img{
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    object-position:center center!important;
    display:block!important;
    transform:scale(1.001)!important;
    transition:transform 420ms cubic-bezier(.2,.7,.2,1)!important;
  }

  .events-category-copy-v2,
  .events-category-footer-v2{
    position:relative!important;
    z-index:2!important;
  }

  .events-category-copy-v2{
    display:flex!important;
    flex-direction:column!important;
    align-items:flex-start!important;
    justify-content:flex-end!important;
    min-height:0!important;
  }

  .events-category-count-v2{
    display:inline-flex!important;
    align-items:center!important;
    min-height:30px!important;
    margin:0 0 14px!important;
    padding:0 11px!important;
    border:1px solid rgba(255,255,255,.30)!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.14)!important;
    color:#fff8ee!important;
    backdrop-filter:blur(12px)!important;
    -webkit-backdrop-filter:blur(12px)!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:11px!important;
    line-height:1!important;
    font-weight:800!important;
    letter-spacing:-.01em!important;
    box-shadow:0 8px 22px rgba(0,0,0,.10)!important;
  }

  .events-category-card-v2 h3{
    max-width:92%!important;
    margin:0!important;
    color:#fff8ee!important;
    font-family:"Cormorant Garamond","Playfair Display",Georgia,"Times New Roman",serif!important;
    font-size:clamp(43px,3.55vw,56px)!important;
    line-height:.92!important;
    letter-spacing:-.036em!important;
    font-weight:500!important;
    text-shadow:0 3px 18px rgba(0,0,0,.28)!important;
  }

  .events-category-card-v2 p{
    max-width:540px!important;
    margin:10px 0 0!important;
    color:rgba(255,248,238,.90)!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:13.5px!important;
    line-height:1.42!important;
    font-weight:600!important;
    text-shadow:0 2px 12px rgba(0,0,0,.24)!important;
  }

  .events-category-footer-v2{
    width:100%!important;
    margin-top:22px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:14px!important;
    color:#fff8ee!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:12.5px!important;
    line-height:1!important;
    font-weight:800!important;
  }

  .events-category-arrow-v2{
    width:38px!important;
    height:38px!important;
    min-width:38px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    border:1px solid rgba(255,255,255,.36)!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.12)!important;
    color:#fff8ee!important;
    font-size:19px!important;
    font-weight:500!important;
    backdrop-filter:blur(10px)!important;
    -webkit-backdrop-filter:blur(10px)!important;
    transition:background 160ms ease,transform 160ms ease!important;
  }

  @media(hover:hover) and (pointer:fine){
    .events-category-card-v2:hover{
      transform:translateY(-3px)!important;
      border-color:rgba(0,103,95,.26)!important;
      box-shadow:0 26px 62px rgba(15,23,42,.16)!important;
    }

    .events-category-card-v2:hover .events-category-media img{
      transform:scale(1.045)!important;
    }

    .events-category-card-v2:hover .events-category-arrow-v2{
      background:rgba(255,255,255,.22)!important;
      transform:translateX(2px)!important;
    }
  }

  @media(min-width:760px) and (max-width:980px){
    .events-category-grid-v2{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:16px!important;
    }

    .events-category-card-v2{
      min-height:320px!important;
      padding:22px!important;
      border-radius:26px!important;
    }

    .events-category-card-v2 h3{
      font-size:clamp(38px,5vw,46px)!important;
    }
  }

  @media(max-width:759px){
    .events-categories-v2{
      padding:28px 0 34px!important;
    }

    .events-categories-head-v2{
      margin-bottom:17px!important;
    }

    .events-categories-title-v2{
      font-size:clamp(42px,12vw,54px)!important;
      line-height:.95!important;
    }

    .events-categories-copy-v2{
      font-size:13px!important;
      margin-top:9px!important;
    }

    .events-category-grid-v2{
      grid-template-columns:1fr!important;
      gap:14px!important;
    }

    .events-category-card-v2{
      min-height:285px!important;
      padding:20px!important;
      border-radius:24px!important;
    }

    .events-category-count-v2{
      min-height:28px!important;
      margin-bottom:12px!important;
      font-size:10.5px!important;
    }

    .events-category-card-v2 h3{
      max-width:100%!important;
      font-size:clamp(39px,11vw,48px)!important;
    }

    .events-category-card-v2 p{
      font-size:12.5px!important;
      margin-top:8px!important;
    }

    .events-category-footer-v2{
      margin-top:18px!important;
      font-size:12px!important;
    }

    .events-category-arrow-v2{
      width:36px!important;
      height:36px!important;
      min-width:36px!important;
    }
  }



  /* =========================================================
     LOOP EVENTS HERO FULL WIDTH OVERLAY FIX V1.2
     V1.1 moved the gradient onto the max-width content overlay.
     On very wide screens that exposed raw-image gutters at both
     sides. Put the gradient on a full-bleed hero pseudo-layer
     while preserving the 1700px content alignment.
     ========================================================= */
  .events-discovery-hero-v11::after{
    content:""!important;
    display:block!important;
    position:absolute!important;
    inset:0!important;
    z-index:1!important;
    pointer-events:none!important;
    background:
      linear-gradient(
        90deg,
        rgba(0,48,42,.98) 0%,
        rgba(0,48,42,.95) 22%,
        rgba(0,48,42,.88) 34%,
        rgba(0,48,42,.68) 46%,
        rgba(0,48,42,.36) 58%,
        rgba(0,48,42,.12) 72%,
        rgba(0,48,42,.03) 100%
      )!important;
  }

  .events-discovery-hero-v11 .events-discovery-hero-overlay-v1{
    z-index:2!important;
    background:transparent!important;
  }

  @media(max-width:899px){
    .events-discovery-hero-v11::after{
      background:
        linear-gradient(
          180deg,
          rgba(0,48,42,.94) 0%,
          rgba(0,48,42,.88) 43%,
          rgba(0,48,42,.72) 70%,
          rgba(0,48,42,.55) 100%
        )!important;
    }
  }



  /* =========================================================
     LOOP EVENTS HERO BRAND ASSETS V1.3
     Final branded Events hero:
     - clean Loop-generated desktop + mobile imagery
     - tighter vertical rhythm
     - lighter cinematic brand tint
     - picture/source responsive asset selection
     Categories V2 remain unchanged.
     ========================================================= */
  .events-discovery-hero-v11{
    height:clamp(460px,28vw,500px)!important;
    min-height:clamp(460px,28vw,500px)!important;
    max-height:500px!important;
  }

  .events-discovery-picture-v13{
    position:absolute!important;
    inset:0!important;
    z-index:0!important;
    display:block!important;
    width:100%!important;
    height:100%!important;
    overflow:hidden!important;
    pointer-events:none!important;
  }

  .events-discovery-picture-v13 .events-discovery-bg-v11{
    position:absolute!important;
    inset:0!important;
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    object-position:61% 55%!important;
    display:block!important;
  }

  .events-discovery-hero-v11::after{
    background:
      linear-gradient(
        90deg,
        rgba(0,61,54,.78) 0%,
        rgba(0,61,54,.68) 20%,
        rgba(0,61,54,.47) 37%,
        rgba(0,61,54,.23) 52%,
        rgba(0,61,54,.07) 68%,
        rgba(0,61,54,0) 82%
      )!important;
  }

  .events-discovery-hero-overlay-v1{
    padding:
      34px
      clamp(28px,8.9vw,154px)
      30px!important;
  }

  .events-discovery-eyebrow-v1{
    margin-bottom:14px!important;
  }

  .events-discovery-subtitle-v1{
    margin-top:18px!important;
  }

  .events-discovery-actions-v1{
    margin-top:22px!important;
    gap:9px!important;
  }

  .events-discovery-primary-v1{
    min-height:56px!important;
  }

  .events-discovery-support-v1{
    margin-top:0!important;
  }

  @media(min-width:900px) and (max-width:1180px){
    .events-discovery-hero-v11{
      height:470px!important;
      min-height:470px!important;
      max-height:470px!important;
    }

    .events-discovery-picture-v13 .events-discovery-bg-v11{
      object-position:60% 54%!important;
    }

    .events-discovery-hero-overlay-v1{
      padding:30px clamp(30px,6vw,66px) 28px!important;
    }
  }

  @media(max-width:899px){
    .events-discovery-hero-v11,
    body.is-mobile-app .events-discovery-hero-v11,
    html.is-mobile-app .events-discovery-hero-v11{
      min-height:460px!important;
      height:460px!important;
      max-height:460px!important;
    }

    .events-discovery-picture-v13 .events-discovery-bg-v11{
      object-position:58% 58%!important;
    }

    .events-discovery-hero-v11::after{
      background:
        linear-gradient(
          180deg,
          rgba(0,58,51,.84) 0%,
          rgba(0,58,51,.73) 42%,
          rgba(0,58,51,.52) 72%,
          rgba(0,58,51,.38) 100%
        )!important;
    }

    .events-discovery-hero-overlay-v1,
    body.is-mobile-app .events-discovery-hero-overlay-v1,
    html.is-mobile-app .events-discovery-hero-overlay-v1{
      min-height:460px!important;
      padding:28px 18px 24px!important;
    }

    .events-discovery-eyebrow-v1{
      margin-bottom:12px!important;
    }

    .events-discovery-subtitle-v1{
      margin-top:15px!important;
    }

    .events-discovery-actions-v1{
      margin-top:19px!important;
      gap:8px!important;
    }

    .events-discovery-primary-v1{
      min-height:52px!important;
    }
  }

  @media(max-width:430px){
    .events-discovery-hero-v11{
      min-height:470px!important;
      height:470px!important;
      max-height:470px!important;
    }

    .events-discovery-hero-overlay-v1{
      min-height:470px!important;
      padding-top:24px!important;
      padding-bottom:22px!important;
    }

    .events-discovery-picture-v13 .events-discovery-bg-v11{
      object-position:57% 60%!important;
    }
  }



  /* =========================================================
     LOOP EVENTS BUSINESS DISCOVERY V3
     Align featured businesses with Categories V2:
     - same 1240px content axis
     - Cormorant editorial section headline
     - premium event-specific business cards
     - adaptive grid for 1 / 2 / 3+ businesses
     - responsive desktop/tablet/mobile
     ========================================================= */
  .events-business-discovery-v3{
    width:min(100%,1240px)!important;
    margin:0 auto!important;
    padding:42px 0 56px!important;
    border-top:1px solid rgba(203,213,225,.78)!important;
  }

  .events-business-head-v3{
    margin-bottom:24px!important;
    align-items:flex-end!important;
  }

  .events-business-title-v3{
    max-width:800px!important;
    margin:0!important;
    color:#0f172a!important;
    font-family:"Cormorant Garamond","Playfair Display",Georgia,"Times New Roman",serif!important;
    font-size:clamp(48px,4.25vw,64px)!important;
    line-height:.94!important;
    letter-spacing:-.035em!important;
    font-weight:500!important;
    text-wrap:balance!important;
  }

  .events-business-copy-v3{
    max-width:700px!important;
    margin-top:11px!important;
    color:#64748b!important;
    font-size:14px!important;
    line-height:1.48!important;
    font-weight:650!important;
    letter-spacing:-.012em!important;
  }

  .events-business-count-v3{
    min-width:148px!important;
    padding:12px 14px!important;
    border:1px solid #e2e8f0!important;
    border-radius:16px!important;
    background:#fff!important;
    display:flex!important;
    align-items:center!important;
    gap:8px!important;
    box-shadow:0 8px 22px rgba(15,23,42,.04)!important;
  }

  .events-business-count-v3 strong{
    color:#00675f!important;
    font-size:20px!important;
    line-height:1!important;
    font-weight:950!important;
  }

  .events-business-count-v3 span{
    color:#64748b!important;
    font-size:11.5px!important;
    line-height:1.2!important;
    font-weight:780!important;
  }

  .events-business-grid-v3{
    display:grid!important;
    gap:20px!important;
    align-items:stretch!important;
  }

  .events-business-grid-v3.is-single{
    grid-template-columns:minmax(0,min(430px,38%))!important;
    justify-content:start!important;
  }

  .events-business-grid-v3.is-double{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }

  .events-business-grid-v3.is-multi{
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
  }

  .events-business-card-v3{
    min-width:0!important;
    height:100%!important;
    border:1px solid rgba(226,232,240,.96)!important;
    border-radius:26px!important;
    background:#fff!important;
    overflow:hidden!important;
    box-shadow:0 18px 42px rgba(15,23,42,.075)!important;
    display:flex!important;
    flex-direction:column!important;
    cursor:pointer!important;
    outline:none!important;
    transition:transform 180ms ease,box-shadow 180ms ease,border-color 180ms ease!important;
  }

  .events-business-card-v3:focus-visible{
    border-color:rgba(0,103,95,.52)!important;
    box-shadow:0 0 0 4px rgba(0,103,95,.10),0 20px 48px rgba(15,23,42,.10)!important;
  }

  .events-business-cover-v3{
    min-height:210px!important;
    height:210px!important;
    padding:14px!important;
    display:flex!important;
    align-items:flex-start!important;
    justify-content:stretch!important;
    background-size:cover!important;
    background-position:center!important;
    position:relative!important;
  }

  .events-business-cover-v3::after{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    background:linear-gradient(180deg,rgba(15,23,42,.08) 0%,rgba(15,23,42,.02) 52%,rgba(15,23,42,.16) 100%)!important;
    pointer-events:none!important;
  }

  .events-business-cover-top-v3{
    width:100%!important;
    position:relative!important;
    z-index:1!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:8px!important;
  }

  .events-business-price-v3,
  .events-business-category-v3{
    min-height:30px!important;
    display:inline-flex!important;
    align-items:center!important;
    padding:0 11px!important;
    border:1px solid rgba(255,255,255,.62)!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.91)!important;
    color:#0f172a!important;
    font-size:10.5px!important;
    line-height:1!important;
    font-weight:900!important;
    box-shadow:0 8px 20px rgba(15,23,42,.08)!important;
    backdrop-filter:blur(10px)!important;
    -webkit-backdrop-filter:blur(10px)!important;
  }

  .events-business-category-v3{
    background:rgba(236,253,245,.92)!important;
    color:#00675f!important;
  }

  .events-business-body-v3{
    flex:1 1 auto!important;
    min-width:0!important;
    padding:18px!important;
    display:flex!important;
    flex-direction:column!important;
  }

  .events-business-meta-v3{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:10px!important;
    color:#64748b!important;
    font-size:10.5px!important;
    line-height:1.25!important;
    font-weight:780!important;
  }

  .events-business-meta-v3 span:first-child{
    min-width:0!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
  }

  .events-business-card-v3 h3{
    margin:10px 0 0!important;
    color:#0f172a!important;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-size:20px!important;
    line-height:1.08!important;
    letter-spacing:-.035em!important;
    font-weight:900!important;
  }

  .events-business-tagline-v3{
    margin:7px 0 0!important;
    color:#64748b!important;
    font-size:12.5px!important;
    line-height:1.42!important;
    font-weight:650!important;
  }

  .events-business-tags-v3{
    margin-top:12px!important;
    gap:6px!important;
  }

  .events-business-tags-v3 span{
    padding:6px 9px!important;
    border-radius:999px!important;
    background:#ecfdf5!important;
    color:#00675f!important;
    font-size:10.5px!important;
    font-weight:850!important;
  }

  .events-business-feature-v3{
    margin-top:14px!important;
    padding:11px 12px!important;
    border:1px solid #e2e8f0!important;
    border-radius:14px!important;
    background:#f8fafc!important;
    display:grid!important;
    gap:2px!important;
  }

  .events-business-feature-v3 > span{
    color:#64748b!important;
    font-size:9.5px!important;
    line-height:1!important;
    font-weight:850!important;
    text-transform:uppercase!important;
    letter-spacing:.08em!important;
  }

  .events-business-feature-v3 strong{
    color:#0f172a!important;
    font-size:12.5px!important;
    line-height:1.22!important;
    font-weight:900!important;
  }

  .events-business-feature-v3 small{
    color:#64748b!important;
    font-size:10.5px!important;
    line-height:1.2!important;
    font-weight:700!important;
  }

  .events-business-cta-v3{
    margin-top:auto!important;
    padding-top:15px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:12px!important;
    color:#00675f!important;
    font-size:11.5px!important;
    line-height:1!important;
    font-weight:900!important;
  }

  .events-business-cta-v3 span:last-child{
    width:34px!important;
    height:34px!important;
    min-width:34px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    border:1px solid rgba(0,103,95,.18)!important;
    border-radius:999px!important;
    background:#ecfdf5!important;
    font-size:17px!important;
    transition:transform 160ms ease,background 160ms ease!important;
  }

  @media(hover:hover) and (pointer:fine){
    .events-business-card-v3:hover{
      transform:translateY(-3px)!important;
      border-color:rgba(0,103,95,.22)!important;
      box-shadow:0 26px 58px rgba(15,23,42,.12)!important;
    }

    .events-business-card-v3:hover .events-business-cta-v3 span:last-child{
      transform:translateX(2px)!important;
      background:#dcfce7!important;
    }
  }

  @media(min-width:760px) and (max-width:1080px){
    .events-business-grid-v3.is-single{
      grid-template-columns:minmax(0,min(430px,58%))!important;
    }

    .events-business-grid-v3.is-multi{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
    }
  }

  @media(max-width:759px){
    .events-business-discovery-v3{
      padding:30px 0 38px!important;
    }

    .events-business-head-v3{
      display:block!important;
      margin-bottom:17px!important;
    }

    .events-business-title-v3{
      font-size:clamp(42px,12vw,54px)!important;
      line-height:.95!important;
    }

    .events-business-title-break-v3{
      display:none!important;
    }

    .events-business-copy-v3{
      margin-top:9px!important;
      font-size:13px!important;
    }

    .events-business-count-v3{
      width:max-content!important;
      min-width:0!important;
      margin-top:14px!important;
      padding:9px 11px!important;
      border-radius:14px!important;
    }

    .events-business-grid-v3.is-single,
    .events-business-grid-v3.is-double,
    .events-business-grid-v3.is-multi{
      grid-template-columns:1fr!important;
      gap:14px!important;
    }

    .events-business-card-v3{
      border-radius:22px!important;
    }

    .events-business-cover-v3{
      height:190px!important;
      min-height:190px!important;
    }

    .events-business-body-v3{
      padding:15px!important;
    }
  }



  /* =========================================================
     LOOP EVENTS BUSINESS DISCOVERY V3.1 SINGLE POLISH
     Final home-page refinement:
     - smaller, single-line editorial title on wide desktop
     - hide redundant "1 negocio disponible" badge
     - wider + shorter single-business card
     - tighter section rhythm
     - preserve 2 / 3+ adaptive behavior
     ========================================================= */
  .events-business-discovery-v3{
    padding-top:34px!important;
    padding-bottom:50px!important;
  }

  .events-business-head-v3{
    margin-bottom:18px!important;
    align-items:flex-end!important;
  }

  .events-business-title-v3{
    max-width:980px!important;
    font-size:clamp(44px,3.7vw,56px)!important;
    line-height:.96!important;
    letter-spacing:-.033em!important;
  }

  .events-business-copy-v3{
    max-width:760px!important;
    margin-top:8px!important;
  }

  .events-business-grid-v3.is-single{
    grid-template-columns:minmax(0,520px)!important;
    justify-content:start!important;
  }

  .events-business-grid-v3.is-single .events-business-card-v3{
    max-width:520px!important;
  }

  .events-business-grid-v3.is-single .events-business-cover-v3{
    min-height:185px!important;
    height:185px!important;
  }

  .events-business-grid-v3.is-single .events-business-body-v3{
    padding:16px!important;
  }

  .events-business-grid-v3.is-single .events-business-card-v3 h3{
    margin-top:8px!important;
    font-size:19px!important;
  }

  .events-business-grid-v3.is-single .events-business-tags-v3{
    margin-top:10px!important;
  }

  .events-business-grid-v3.is-single .events-business-feature-v3{
    margin-top:12px!important;
    padding:10px 11px!important;
  }

  .events-business-grid-v3.is-single .events-business-cta-v3{
    padding-top:12px!important;
  }

  @media(min-width:760px) and (max-width:1080px){
    .events-business-title-v3{
      max-width:820px!important;
      font-size:clamp(44px,5vw,52px)!important;
    }

    .events-business-grid-v3.is-single{
      grid-template-columns:minmax(0,500px)!important;
    }

    .events-business-grid-v3.is-single .events-business-card-v3{
      max-width:500px!important;
    }
  }

  @media(max-width:759px){
    .events-business-discovery-v3{
      padding-top:28px!important;
      padding-bottom:38px!important;
    }

    .events-business-head-v3{
      margin-bottom:15px!important;
    }

    .events-business-title-v3{
      max-width:100%!important;
      font-size:clamp(42px,12vw,54px)!important;
      line-height:.95!important;
    }

    .events-business-copy-v3{
      margin-top:8px!important;
    }

    .events-business-grid-v3.is-single{
      grid-template-columns:1fr!important;
    }

    .events-business-grid-v3.is-single .events-business-card-v3{
      max-width:none!important;
    }

    .events-business-grid-v3.is-single .events-business-cover-v3{
      min-height:190px!important;
      height:190px!important;
    }
  }



  /* =========================================================
     LOOP EVENTS BUSINESS LOGO FIT V3.2
     Business image semantics:
     - logoUrl/logo/logoImage => preserve the full logo (contain)
     - menuImage fallback     => keep photographic crop (cover)
     This prevents brand marks from being cropped by wide cards.
     ========================================================= */
  .events-business-cover-v3.is-logo{
    background-color:#f8fafc!important;
    background-size:100% 100%,contain!important;
    background-repeat:no-repeat,no-repeat!important;
    background-position:center center,center center!important;
  }

  .events-business-cover-v3.is-logo::after{
    background:linear-gradient(180deg,rgba(15,23,42,.015) 0%,rgba(15,23,42,.025) 100%)!important;
  }

  .events-business-cover-v3.is-photo{
    background-size:100% 100%,cover!important;
    background-repeat:no-repeat,no-repeat!important;
    background-position:center center,center center!important;
  }

  .events-business-grid-v3.is-single .events-business-cover-v3.is-logo{
    min-height:205px!important;
    height:205px!important;
  }

  @media(max-width:759px){
    .events-business-grid-v3.is-single .events-business-cover-v3.is-logo{
      min-height:200px!important;
      height:200px!important;
    }
  }



  /* =========================================================
     LOOP EVENTS BUSINESS GENERIC SERVICE CLEANUP V3.3
     Prevent generic placeholders like "Servicio principal" from
     appearing as if they were real service names.
     ========================================================= */
  .events-business-quick-detail-v33{
    width:max-content!important;
    max-width:100%!important;
    margin-top:12px!important;
    padding:8px 11px!important;
    border:1px solid #e2e8f0!important;
    border-radius:999px!important;
    background:#f8fafc!important;
    color:#475569!important;
    font-size:11px!important;
    line-height:1!important;
    font-weight:820!important;
    letter-spacing:-.01em!important;
  }

  .events-business-grid-v3.is-single .events-business-quick-detail-v33{
    margin-top:10px!important;
  }

  @media(max-width:759px){
    .events-business-quick-detail-v33{
      margin-top:10px!important;
      padding:8px 10px!important;
      font-size:10.5px!important;
    }
  }


`;

function BusinessCard({ business, onClick }) {
  const cover = business.imageUrl
    ? `linear-gradient(180deg, rgba(15,23,42,0.03), rgba(15,23,42,0.10)), url("${business.imageUrl}")`
    : "linear-gradient(135deg, #ecfdf5 0%, #ffffff 48%, #fef3c7 100%)";

  const primaryBadge = business.badges?.[0] || "Eventos";
  const visibleTags = (business.characters || []).slice(0, 2);
  const primaryPackage = (business.packages || [])[0] || null;
  const primaryPackageTitle = String(primaryPackage?.title || "").trim();
  const primaryPackageTitleNormalized = normalizeText(primaryPackageTitle);

  const genericPrimaryPackageTitles = new Set([
    "",
    "servicio principal",
    "servicio",
    "service",
    "principal service",
    "paquete",
    "package",
    "paquete principal",
    "main package",
  ]);

  const hasSpecificPrimaryPackageTitle =
    Boolean(primaryPackageTitle) &&
    !genericPrimaryPackageTitles.has(primaryPackageTitleNormalized);

  const primaryPackageDetailParts = [
    primaryPackage?.duration,
    primaryPackage?.price,
  ].filter((value) => {
    const normalized = normalizeText(value || "");
    return Boolean(
      normalized &&
      normalized !== "a consultar" &&
      normalized !== "cotizar" &&
      normalized !== "consultar" &&
      normalized !== "-"
    );
  });

  const primaryPackageDetails = primaryPackageDetailParts.join(" · ");
  const showPrimaryPackageFeature = Boolean(
    primaryPackage &&
    (hasSpecificPrimaryPackageTitle || primaryPackageDetails)
  );

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <article
      className="events-business-card events-business-card-v3"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Ver negocio ${business.name || "de eventos"}`}
    >
      <div
        className={`events-business-cover events-business-cover-v3 ${business.imageDisplayMode === "logo" ? "is-logo" : "is-photo"}`}
        style={{ background: cover }}
      >
        <div className="events-business-cover-top-v3">
          <span className="events-business-price-v3">
            {business.fromPrice ? `Desde ${business.fromPrice}` : "Cotizar"}
          </span>
          <span className="events-business-category-v3">{primaryBadge}</span>
        </div>
      </div>

      <div className="events-business-body events-business-body-v3">
        <div className="events-business-meta events-business-meta-v3">
          <span>{business.location || "Panamá"}</span>
          <span>{business.rating ? `★ ${business.rating}` : "Nuevo"}</span>
        </div>

        <h3>{business.name}</h3>
        {business.tagline ? <p className="events-business-tagline-v3">{business.tagline}</p> : null}

        {visibleTags.length ? (
          <div className="events-badges events-business-tags-v3">
            {visibleTags.map((x) => <span key={x}>{x}</span>)}
          </div>
        ) : null}

        {showPrimaryPackageFeature ? (
          hasSpecificPrimaryPackageTitle ? (
            <div className="events-business-feature-v3">
              <span>Servicio principal</span>
              <strong>{primaryPackageTitle}</strong>
              {primaryPackageDetails ? <small>{primaryPackageDetails}</small> : null}
            </div>
          ) : (
            <div className="events-business-quick-detail-v33">
              <span>{primaryPackageDetails}</span>
            </div>
          )
        ) : null}

        <div className="events-business-cta-v3">
          <span>Ver negocio</span>
          <span aria-hidden="true">→</span>
        </div>
      </div>
    </article>
  );
}

export default function EventsHomePage() {
  const navigate = useNavigate();
  const businessesRef = useRef(null);
  const [businesses, setBusinesses] = useState(() => readJsonCache(EVENTS_CACHE_KEY, []).filter(isPublicBusiness));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true); setLoadError(null);
      try { const list = await fetchEventBusinesses(); if (!mounted) return; setBusinesses(list); writeJsonCache(EVENTS_CACHE_KEY, list); }
      catch (err) { console.warn("[EventsHomePage] fetch events failed", err); if (mounted) setLoadError(err); }
      finally { if (mounted) setLoading(false); }
    }
    load(); return () => { mounted = false; };
  }, []);

  const displayBusinesses = useMemo(() => Array.isArray(businesses) ? businesses.filter(isPublicBusiness) : [], [businesses]);
  const categoryCounts = useMemo(() => {
    const out = {};
    for (const c of EVENT_CATEGORIES) out[c.id] = displayBusinesses.filter((b) => categoryMatchesBusiness(c, b)).length;
    return out;
  }, [displayBusinesses]);
  const scrollToBusinesses = () => { try { businessesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) {} };
  const formatCategoryCount = (count) => {
    const n = Number(count || 0);
    if (n === 1) return "1 opción disponible";
    return `${n} opciones disponibles`;
  };

  return (
    <main className="events-page"><style>{eventStyles}</style>
      <section
        className="events-discovery-hero-v1 events-discovery-hero-v11"
        aria-label="Eventos en Loop"
      >
        <picture className="events-discovery-picture-v13" aria-hidden="true">
          <source media="(max-width: 759px)" srcSet={eventHeroMobile} />
          <img
            className="events-discovery-bg-v11"
            src={eventHeroDesktop}
            alt=""
            aria-hidden="true"
            draggable="false"
          />
        </picture>
        <div className="events-discovery-hero-overlay-v1">
          <div className="events-discovery-copy-v1">
            <div className="events-discovery-eyebrow-v1">EVENTOS</div>

            <h1 className="events-discovery-title-v1">
              Haz que tu evento<br />se sienta inolvidable.
            </h1>

            <p className="events-discovery-subtitle-v1">
              Encuentra personajes, fotógrafos, shows y experiencias para celebrar a tu manera.
            </p>

            <div className="events-discovery-actions-v1">
              <button
                type="button"
                className="events-discovery-primary-v1"
                onClick={scrollToBusinesses}
              >
                <span>Explorar experiencias</span>
                <span className="events-discovery-primary-arrow-v1" aria-hidden="true">→</span>
              </button>
              <span className="events-discovery-support-v1">
                Reserva, cotiza o contacta directamente con cada negocio.
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="events-shell events-shell-after-hero-v1">
      <section className="events-section events-categories-v2">
        <div className="events-section-head events-categories-head-v2">
          <div>
            <div className="events-section-kicker">Explora por categoría</div>
            <h2 className="events-section-title events-categories-title-v2">Elige el tipo de experiencia.</h2>
            <p className="events-section-copy events-categories-copy-v2">Empieza por una categoría y descubre opciones, paquetes y negocios para tu evento.</p>
          </div>
        </div>

        <div className="events-category-grid events-category-grid-v2">
          {EVENT_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className="events-category-card events-category-card-v2"
              style={{ "--event-bg": cat.image }}
              onClick={() => navigate(`/events/${cat.id}`)}
            >
              <div className="events-category-copy-v2">
                <div className="events-category-media">
                  {cat.imageUrl ? <img src={cat.imageUrl} alt={cat.title} /> : <span>{cat.emoji}</span>}
                </div>

                <span className="events-category-count-v2">{formatCategoryCount(categoryCounts[cat.id])}</span>
                <h3>{cat.title}</h3>
                <p>{cat.subtitle}</p>
              </div>

              <footer className="events-category-footer-v2">
                <span>Descubrir opciones</span>
                <span className="events-category-arrow-v2" aria-hidden="true">→</span>
              </footer>
            </button>
          ))}
        </div>
      </section>
      <section className="events-section events-business-discovery-v3" ref={businessesRef}>
        <div className="events-section-head events-business-head-v3">
          <div>
            <div className="events-section-kicker">Empresas destacadas</div>
            <h2 className="events-section-title events-business-title-v3">
              Negocios destacados para tu evento.
            </h2>
            <p className="events-section-copy events-business-copy-v3">
              {displayBusinesses.length
                ? "Descubre proveedores reales en Loop para celebrar, producir y crear experiencias memorables."
                : "Aún no hay empresas reales publicadas en Eventos. Puedes empezar cargando negocios desde Crear negocio."}
            </p>
          </div>

          {displayBusinesses.length > 1 ? (
            <div className="events-business-count-v3">
              <strong>{displayBusinesses.length}</strong>
              <span>negocios disponibles</span>
            </div>
          ) : null}
        </div>

        {loading && displayBusinesses.length === 0 ? (
          <div className="events-loading-card">Cargando empresas de eventos...</div>
        ) : null}

        {!loading && loadError && displayBusinesses.length === 0 ? (
          <div className="events-empty-card">No pudimos cargar empresas reales todavía.</div>
        ) : null}

        {!loading && displayBusinesses.length === 0 ? (
          <div className="events-empty-card">No hay empresas de eventos publicadas todavía.</div>
        ) : null}

        <div
          className={`events-business-grid events-business-grid-v3 ${displayBusinesses.length === 1 ? "is-single" : displayBusinesses.length === 2 ? "is-double" : "is-multi"}`}
        >
          {displayBusinesses.map((business) => (
            <BusinessCard
              key={business.id}
              business={business}
              onClick={() => navigate(`/business/${business.businessId || business.id}`)}
            />
          ))}
        </div>
      </section>
    </div></main>
  );
}
